import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, FormGroup, Input, Row } from "reactstrap";
import {
  CheckVerificationCodeResponseDto,
  LoginRequestDto,
} from "./types";
import { MessageResultState } from "../../../types/general-types";
import { inject, observer } from "mobx-react";
import LoginStore from "./store";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import Button from "../../../components/Button";
import { BiLogIn } from "react-icons/bi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import {
  resetAccounts,
  setSessionKey,
  setUser,
} from "../../../utils/SsoSession";
import VerifyPage from "./verify-login";
import { Controller, useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import LanguageSelection from "../../../components/Layout/partials/Header/parts/languageSelection";
import { LoginResponseDto } from "./types";
import MandatoryPasswordRenewalStore from "../AccountActions/mandatory-password-renewal/store";
import MandatoryPasswordRenewalModal from "../AccountActions/mandatory-password-renewal/mandatory-password-renewal-modal";

interface IPage {
  LoginStore?: LoginStore;
  MandatoryPasswordRenewalStore?: MandatoryPasswordRenewalStore;
}
const LoginPage: FC<IPage> = inject("LoginStore", "MandatoryPasswordRenewalStore")(
  observer((props: IPage) => {
    const { LoginStore: store, MandatoryPasswordRenewalStore: renewalStore } =
      props;
    const { t, i18n } = useTranslation();
    const [animationShow, setAnimationShow] = useState<boolean>(false);
    const [disabledLogin, setDisabledLogin] = useState<boolean>(false);
    const [muted, setMuted] = useState<boolean>(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null); // reCAPTCHA referansı

    const queryParams = new URLSearchParams(window.location.search);

    const {
      control,
      handleSubmit,
      errors,
      formState,
      watch,
      setValue,
      getValues,
      register,
    } = useForm<LoginRequestDto>({
      defaultValues: {
        captchaToken: "",
        username: "",
        password: "",
      },
    });

    const [clientId] = useState(queryParams.get("clientId") ?? null);
    const watchedUsername = watch("username");
    const watchedPassword = watch("password");

    const [verifyModalToggle, setVerifyModalToggle] = useState(false);
    const [renewalModalToggle, setRenewalModalToggle] = useState(false);
    const [renewalUsername, setRenewalUsername] = useState<string>("");
    const [renewalPassword, setRenewalPassword] = useState<string>("");
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [verifyMode, setVerifyMode] = useState<"login" | "blocked">("login");
    const [blockedMaskedPhoneNumber, setBlockedMaskedPhoneNumber] = useState<
      string | null | undefined
    >(null);
    const toggleVerifyModalToggle = useCallback(
      (successResult?: CheckVerificationCodeResponseDto) => {
        setVerifyModalToggle((prev) => !prev);
        if (!successResult) {
          // Modal kapanırken login ekranını ilk duruma döndür.
          setVerifyMode("login");
          setBlockedMaskedPhoneNumber(null);
          recaptchaRef.current?.reset();
          setValue("captchaToken", "");
        }
        if (successResult) {
          const returnUrl = queryParams.get("returnUrl");
          setDisabledLogin(true);
          setAnimationShow(true);
          let videoElement: HTMLVideoElement | null =
            document.querySelector("#video_2");
          videoElement?.play();
          setDisabledLogin(true);
          setTimeout(() => {
            setDisabledLogin(true);
            setSessionKey(successResult.sessionKey);
            setUser(successResult.user);
            resetAccounts({
              sessionKey: successResult.sessionKey,
              userName: successResult.user?.username,
              email: successResult.user?.email,
              userRoles: successResult.user?.userRoles || [],
              isRoot: true,
            });
            if (successResult.redirectUrl) {
              let path =
                successResult.redirectUrl +
                ("?sessionKey=" + successResult.sessionKey) +
                (returnUrl && returnUrl !== "/"
                  ? "&returnUrl=" + returnUrl
                  : "");
              window.location.href = path;
            } else window.location.href = "portal-selection";
            setDisabledLogin(true);
          }, 1757);
        }
      },
      [queryParams, setValue]
    );

    useEffect(() => {
      let videoElement: HTMLVideoElement | null =
        document.querySelector("#video_1");
      if (videoElement) {
        setMuted(false);
        setMuted(true);
      }
    }, []);

    const onSubmit = useCallback(
      async (input: LoginRequestDto) => {
        if (input.password && input.username && input.captchaToken) {
          await store
            ?.Login(input)
            .then(async (response) => {
              if (response.state === MessageResultState.SUCCESS) {
                const renewalStatus = await renewalStore
                  ?.CheckStatus({
                    username: input.username,
                    password: input.password,
                  })
                  .catch(() => undefined);
                if (
                  renewalStatus?.state === MessageResultState.SUCCESS &&
                  renewalStatus.result?.required
                ) {
                  setRenewalUsername(
                    renewalStatus.result.username ?? input.username
                  );
                  setRenewalPassword(renewalStatus.result.password ?? "");
                  setRenewalModalToggle(true);
                } else {
                  setVerifyMode("login");
                  setBlockedMaskedPhoneNumber(null);
                  setVerifyModalToggle(true);
                }
              } else if (
                (response.result as LoginResponseDto | null | undefined)
                  ?.requiresBlockedUnblockVerification
              ) {
                setVerifyMode("blocked");
                setBlockedMaskedPhoneNumber(
                  (response.result as LoginResponseDto).maskedPhoneNumber
                );
                setVerifyModalToggle(true);
              } else {
                recaptchaRef.current?.reset(); // reCAPTCHA’yı sıfırla

                let videoElement: HTMLVideoElement | null =
                  document.querySelector("#video_2");
                videoElement?.load();
                setDisabledLogin(false);
                setAnimationShow(false);
              }
            })
            .catch((err) => {
              setDisabledLogin(false);
            });
        }
      },
      [store, renewalStore]
    );

    const animation = useMemo(() => {
      return (
        <>
          <div className="h-100 bg-plum-plate bg-animation">
            <video
              style={{
                width: "100vw",
                height: "100vh",
                position: "fixed",
                objectFit: "cover",
                top: 0,
                left: 0,
                zIndex: animationShow ? -1 : -2,
              }}
              src="https://strsecuritasdigicloud.blob.core.windows.net/upload-1324d8f0-8e85-49cf-8aa2-46b1f147/ui_videos/login_2.mp4"
              controls={false}
              id={"video_2"}
              muted={muted}
            />

            <video
              style={{
                width: "100vw",
                height: "100vh",
                position: "fixed",
                objectFit: "cover",
                top: 0,
                left: 0,
                zIndex: animationShow ? -2 : -1,
              }}
              src="https://strsecuritasdigicloud.blob.core.windows.net/upload-1324d8f0-8e85-49cf-8aa2-46b1f147/ui_videos/login_1.mp4"
              controls={false}
              autoPlay={true}
              muted={muted}
              loop={true}
              id={"video_1"}
            />
            {!disabledLogin ? (
              <div className="d-flex h-100 justify-content-center align-items-center mt-5 content">
                <Col
                  md="4"
                  className="mx-auto app-login-box p-3 mt-5"
                  style={{ backgroundColor: "white", borderRadius: 10 }}
                >
                  <div className=" mx-auto mb-3" />

                  <div className="modal-dialog w-100 mx-auto">
                    <div className="modal-content">
                      <div className="modal-body">
                        <div className="h5 modal-title text-center mb-4">
                          <h4 className="mt-2">
                            <span>
                              {t("Please sign in to your account below.")}
                            </span>
                          </h4>
                        </div>
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <Row>
                            <Col md={12}>
                              <FormGroup>
                                <Controller
                                  name={"username"}
                                  control={control}
                                  rules={{
                                    required: t("*Please fill out this field."),
                                    maxLength: {
                                      value: 100,
                                      message: t(
                                        "The given value should be smaller than {{ value }}.",
                                        { value: 100 }
                                      ),
                                    },
                                  }}
                                  render={(field) => (
                                    <input
                                      {...field}
                                      disabled={formState.isSubmitting}
                                      id={"username"}
                                      className="form-control"
                                      placeholder={t("Username")}
                                      onChange={(e) => {
                                        recaptchaRef.current?.reset(); // reCAPTCHA’yı sıfırla
                                        field.onChange(e.target.value);
                                      }}
                                      // autoFocus={true}
                                    />
                                  )}
                                />
                                {errors.username && (
                                  <div className="invalid-feedback  d-block">
                                    {t(errors.username.message ?? "")}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup>
                                <Controller
                                  name={"password"}
                                  control={control}
                                  rules={{
                                    required: t("*Please fill out this field."),
                                    minLength: {
                                      value: 3,
                                      message: t(
                                        "The given value should be greater than {{ value }}.",
                                        { value: 3 }
                                      ),
                                    },
                                    maxLength: {
                                      value: 100,
                                      message: t(
                                        "The given value should be smaller than {{ value }}.",
                                        { value: 100 }
                                      ),
                                    },
                                  }}
                                  render={(field) => (
                                    <div className="position-relative">
                                      <input
                                        {...field}
                                        disabled={formState.isSubmitting}
                                        id={"password"}
                                        className="form-control"
                                        placeholder={t("Password")}
                                        type={
                                          passwordVisible ? "text" : "password"
                                        }
                                        autoComplete="new-password"
                                        style={{ paddingRight: "40px" }}
                                        onChange={(e) => {
                                          recaptchaRef.current?.reset(); // reCAPTCHA’yı sıfırla
                                          field.onChange(e.target.value);
                                        }}
                                      />
                                      <span
                                        role="button"
                                        tabIndex={-1}
                                        onClick={() =>
                                          setPasswordVisible((p) => !p)
                                        }
                                        style={{
                                          position: "absolute",
                                          top: "50%",
                                          right: "12px",
                                          transform: "translateY(-50%)",
                                          cursor: "pointer",
                                          color: "#6c757d",
                                          zIndex: 5,
                                        }}
                                      >
                                        {passwordVisible ? (
                                          <FaEyeSlash />
                                        ) : (
                                          <FaEye />
                                        )}
                                      </span>
                                    </div>
                                  )}
                                />
                                {errors.password && (
                                  <div className="invalid-feedback  d-block">
                                    {t(errors.password.message ?? "")}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup>
                                <a
                                  className="text-danger fw-bold ms-1"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    window.location.href =
                                      window.location.origin +
                                      "/forgot-password";
                                  }}
                                >
                                  {t("Forgot my password!")!}
                                </a>
                              </FormGroup>
                            </Col>

                            <div className="divider" />

                            <Col md={12}>
                              {watchedUsername && watchedPassword && (
                                <FormGroup className="recaptcha-container">
                                  <Controller
                                    name={"captchaToken"}
                                    control={control}
                                    rules={{
                                      required: t(
                                        "*Please fill out this field."
                                      ),
                                    }}
                                    render={(field) => (
                                      <ReCAPTCHA
                                        {...field}
                                        key={i18n.language} // This will force the component to reset and reload when the language changes
                                        id="captchaToken"
                                        sitekey="6LcM63QqAAAAAHFcTBB7w4yd2SqF4l7EyTDNRZyg" // Buraya Google reCAPTCHA site anahtarınızı ekleyin
                                        onChange={async (value) => {
                                          field.onChange(value);
                                          setValue("captchaToken", value);
                                          if (value) {
                                            await handleSubmit(
                                              async (data: any) =>
                                                await onSubmit(data)
                                            )();
                                          }
                                        }}
                                        hl={i18n.language}
                                        ref={recaptchaRef}
                                      />
                                    )}
                                  />{" "}
                                  {errors.captchaToken && (
                                    <div className="invalid-feedback  d-block">
                                      {t(errors.captchaToken.message ?? "")}
                                    </div>
                                  )}
                                </FormGroup>
                              )}
                            </Col>
                          </Row>
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="text-center opacity-8 mt-3">
                    Copyright &copy; Securitas
                  </div>
                </Col>
              </div>
            ) : (
              <></>
            )}
          </div>
          {verifyModalToggle && (
            <VerifyPage
              LoginStore={store}
              clientId={clientId}
              loginRequestDto={getValues()}
              isShow={verifyModalToggle}
              toggleModal={toggleVerifyModalToggle}
              mode={verifyMode}
              blockedMaskedPhoneNumber={blockedMaskedPhoneNumber}
            />
          )}
          {renewalModalToggle && (
            <MandatoryPasswordRenewalModal
              MandatoryPasswordRenewalStore={renewalStore}
              username={renewalUsername}
              password={renewalPassword}
              isShow={renewalModalToggle}
              onClose={() => setRenewalModalToggle(false)}
              clientId={clientId}
            />
          )}
          {/* <ToastContainer /> */}
        </>
      );
    }, [
      animationShow,
      clientId,
      control,
      disabledLogin,
      errors.captchaToken,
      errors.password,
      errors.username,
      formState.isSubmitting,
      getValues,
      handleSubmit,
      i18n,
      t,
      muted,
      onSubmit,
      setValue,
      store,
      toggleVerifyModalToggle,
      verifyModalToggle,
      verifyMode,
      blockedMaskedPhoneNumber,
      watchedPassword,
      watchedUsername,
      renewalModalToggle,
      renewalUsername,
      renewalPassword,
      renewalStore,
      passwordVisible,
    ]);

    return <div>{animation}</div>;
  })
);

export default LoginPage;
